import { useEffect, useRef, useState } from "react";
import useUploadFile from "../../../../../../Components/FileUploader/fileUploader-hooks";
import { BusinessTypes, IAutocompleteItem, ImageTypes, MeasureTypes, TimeUnitTypes } from "../../../../../../Interfaces/componentTypes";
import { useDirectoriesHook, useGeneralHooks, useToastify } from "../../../../../../Hooks/hooks";
import { useFieldArray, useForm } from "react-hook-form";
import { ICreateMenuItemForm, IMenuItemFormOption } from "../../../../../../Interfaces/formValue-interfaces";
import { useCreateMenuItemMutation, useGetMenuCtegoriesQuery, useGetMenuListQuery, useUpdateMenuItemMutation, useUpdateMenuOrderListMutation } from "../../../../../../API/authApi";
import { ICreateMenuItem, IMenuItemOption, IUpdateMenuItem, IUpdateMenuItemOrderList } from "../../../../../../Interfaces/request-interfaces";
import { IEditMenuItemProps } from "./editMenuItem";
import useLanguagesHooks from "../../../../../../Components/Languages/setLanguage-hooks";

const useEditMenuItem = (props: IEditMenuItemProps) => {
    const { data, onClose, cb } = props;
    const { t, activeLanguage } = useLanguagesHooks();
    const { category_id, id, image } = data;
    const { imageUrl, handleFileInputChange, ImageResponce, imageLoading } = useUploadFile(ImageTypes.CATEGORY);
    const { data: categories } = useGetMenuCtegoriesQuery({ business_type: BusinessTypes.RESTAURANT, company_id: props.company_id });

    const categoryOptions: IAutocompleteItem[] = (categories ?? []).map((cat) => {
        const nameKey = `name_${activeLanguage}` as keyof typeof cat;
        return { id: cat.id, title: (cat[nameKey] as string) || cat.name_en };
    });

    const currentCategory = categoryOptions.find((c) => c.id === props.category_id);
    const [updateItem, { isLoading: updateLoading, isSuccess: updateSuccess, isError: updateError }] = useUpdateMenuItemMutation();
    const [updateClonedItem, { isLoading: cloneUpdateLoading }] = useUpdateMenuItemMutation();
    const [updateItemOrderList] = useUpdateMenuOrderListMutation();
    const [createItem, { isLoading: cloneLoading }] = useCreateMenuItemMutation();
    const { data: menuList } = useGetMenuListQuery(props.company_id);
    const dialogRef = useRef<HTMLDivElement | null>(null);
    const { menuItemMeasureTypes, timeUnits } = useDirectoriesHook();
    const [showTranslations, setShowTranslations] = useState<boolean>(false);
    const { baseURL } = useGeneralHooks();
    const [isFormValid, setIsFormValid] = useState(false);
    const [isFormChanged, setIsFormChanged] = useState(false);
    const { showToastSuccess, showToastError } = useToastify();
    const formValidator = isFormValid && isFormChanged;
    const originalDataRef = useRef<ICreateMenuItemForm>({
        name_hy: data.name_hy,
        name_en: data.name_en,
        name_ru: data.name_ru,
        description_hy: data.description_hy,
        description_en: data.description_en,
        description_ru: data.description_ru,
        cooking_time: data.cooking_time,
        cooking_time_unit: timeUnits.find((unit) => unit.id === data.cooking_time_unit),
        category: currentCategory,
        option: data.option.map((option) => ({
            is_active: option.is_active,
            measure_type: menuItemMeasureTypes.find((type) => type.id === option.measure_type) ?? undefined,
            measure_value: option.measure_value,
            price: option.price,
            id: option.option_id,
        })),
    });

    const {
        register,
        control,
        formState: { errors },
        setValue,
        getValues,
        watch,
    } = useForm<ICreateMenuItemForm>({
        mode: 'all',
        defaultValues: {
            name_hy: data.name_hy,
            name_en: data.name_en,
            name_ru: data.name_ru,
            description_hy: data.description_hy,
            description_en: data.description_en,
            description_ru: data.description_ru,
            cooking_time: data.cooking_time,
            cooking_time_unit: timeUnits.find((unit) => unit.id === data.cooking_time_unit),
            category: currentCategory,
        }
    });
    const { fields, append, remove, update, } = useFieldArray({
        control,
        name: "option"
    });

    const validateForm = (): boolean => {
        const formValues = getValues();
        const { name_hy, option } = formValues;
        if (!name_hy || name_hy.trim() === "") return false;
        for (let i = 0; i < option.length; i++) {
            const { measure_type, price } = option[i];
            if (!measure_type || !price || price.trim() === "") return false;
        }

        return true;
    };


    const detectFormChanges = () => {
        const currentValues = getValues();
        const isChanged = JSON.stringify(currentValues) !== JSON.stringify(originalDataRef.current);
        setIsFormChanged(isChanged)
    };

    useEffect(() => {
        const subscription = watch(() => {
            detectFormChanges();
            const isValid = validateForm();
            setIsFormValid(isValid);
        });
        return () => subscription.unsubscribe();
    }, [watch]);

    useEffect(() => {
        if (categories && categories.length > 0) {
            const nameKey = `name_${activeLanguage}` as keyof typeof categories[0];
            const matched = categories.find((cat) => cat.id === props.category_id);
            if (matched) {
                const option: IAutocompleteItem = { id: matched.id, title: (matched[nameKey] as string) || matched.name_en };
                setValue('category', option);
                originalDataRef.current = { ...originalDataRef.current, category: option };
            }
        }
    }, [categories]);

    useEffect(() => {
        if (data) {
            remove();
            data.option.forEach((option) => {
                const optionField: IMenuItemFormOption = {
                    is_active: option.is_active,
                    measure_type: menuItemMeasureTypes.find((type) => type.id === option.measure_type) ?? undefined,
                    measure_value: option.measure_value,
                    price: option.price,
                    package_cost:option.package_cost,
                    option_id: option.option_id,
                };
                append(optionField)
            })
        }
    }, [data]);

    const addOption = () => {
        append({ measure_type: undefined, measure_value: "", price: undefined, is_active: false })
    };

    const deleteOption = (index: number) => {
        remove(index)
    };

    const handleShowTranslations = () => {
        setShowTranslations(prev => !prev)
    };

    useEffect(() => {
        if (updateSuccess) {
            showToastSuccess(t('Toast.Success.Common'));
            cb();
            onClose();

        } else if (updateError) {
            showToastError(t('Toast.Error.Common'))
        }
    }, [updateSuccess, updateError])

    const onUpdate = () => {
        const options: IMenuItemOption[] = fields.map((option) => {
            return {
                option_id: option.option_id,
                measure_type: option.measure_type?.id as MeasureTypes,
                measure_value: Number(option.measure_value) || 1,
                price: option.price!,
                package_cost:option.package_cost,
                is_active: option.is_active
            };
        })
        const selectedCategory = getValues('category');
        const payload: IUpdateMenuItem = {
            category_id: selectedCategory ? Number(selectedCategory.id) : props.category_id,
            company_id: props.company_id,
            menu_item_id: id,
            image: ImageResponce ? ImageResponce.src : image,
            name_en: getValues().name_en,
            name_ru: getValues().name_ru,
            name_hy: getValues().name_hy,
            description_en: getValues().description_en,
            description_ru: getValues().description_ru,
            description_hy: getValues().description_hy,
            option: options,
            cooking_time: getValues('cooking_time')!,
            cooking_time_unit: getValues('cooking_time_unit')?.id as TimeUnitTypes,
        };
        updateItem(payload)
    };

    const onClone = async (targetCategoryId: number) => {
        try {
            if (!targetCategoryId || targetCategoryId === props.category_id) {
                showToastError(t('Toast.Error.Common'));
                return;
            }

            const options: IMenuItemOption[] = fields.map((option) => ({
                measure_type: option.measure_type?.id as MeasureTypes,
                measure_value: Number(option.measure_value) || 1,
                price: option.price!,
                package_cost: option.package_cost,
                is_active: option.is_active
            }));

            const createPayload: ICreateMenuItem = {
                name: getValues().name_en || getValues().name_hy || getValues().name_ru || data.name_en || data.name_hy || data.name_ru,
                description: getValues().description_en || getValues().description_hy || getValues().description_ru || data.description_en || data.description_hy || data.description_ru,
                image: ImageResponce ? ImageResponce.src : image,
                option: options,
                cooking_time: getValues('cooking_time')!,
                cooking_time_unit: getValues('cooking_time_unit')?.id as TimeUnitTypes,
                category_id: targetCategoryId,
                company_id: props.company_id
            };

            const createdItem: any = await createItem(createPayload).unwrap();
            const createdItemId = Number(
                createdItem?.id ??
                createdItem?.result?.id ??
                createdItem?.data?.id
            );

            if (!createdItemId) {
                throw new Error('Clone item id not found in response');
            }

            const updatePayload: IUpdateMenuItem = {
                menu_item_id: createdItemId,
                category_id: targetCategoryId,
                company_id: props.company_id,
                name_en: getValues().name_en || data.name_en,
                name_ru: getValues().name_ru || data.name_ru,
                name_hy: getValues().name_hy || data.name_hy,
                description_en: getValues().description_en || data.description_en,
                description_ru: getValues().description_ru || data.description_ru,
                description_hy: getValues().description_hy || data.description_hy,
                option: options,
                cooking_time: getValues('cooking_time')!,
                cooking_time_unit: getValues('cooking_time_unit')?.id as TimeUnitTypes,
            };

            await updateClonedItem(updatePayload).unwrap();

            const targetCategory = menuList?.find((item) => item.category.id === targetCategoryId);
            const baseOrderList = targetCategory?.order_list ?? [];
            const normalizedOrderList = baseOrderList
                .map((itemId) => Number(itemId))
                .filter(Boolean);
            const nextOrderList = normalizedOrderList.includes(createdItemId)
                ? normalizedOrderList
                : [...normalizedOrderList, createdItemId];

            const orderPayload: IUpdateMenuItemOrderList = {
                company_id: props.company_id,
                category_id: targetCategoryId,
                order_list: nextOrderList
            };
            await updateItemOrderList(orderPayload).unwrap();

            showToastSuccess(t('Toast.Success.Common'));
            cb();
        } catch {
            showToastError(t('Toast.Error.Common'));
        }
    };

    return {
        fields,
        errors,
        baseURL,
        control,
        imageUrl,
        dialogRef,
        timeUnits,
        imageLoading,
        updateLoading,
        cloneLoading: cloneLoading || cloneUpdateLoading,
        formValidator,
        showTranslations,
        categoryOptions,
        update,
        register,
        onUpdate,
        onClone,
        setValue,
        getValues,
        addOption,
        deleteOption,
        handleFileInputChange,
        handleShowTranslations,
    }
};

export default useEditMenuItem